import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LegalHold } from '@cloudsign/shared';
import type { DataSubject } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Badge, Button, Card, CheckboxField, EmptyState, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { holdsKey, useHolds } from './api';

/** Legal holds: records named here are never deleted (retention or erasure) until the hold is released. */
export function HoldsTab({ draft, onDraftDone }: { draft: DataSubject | null; onDraftDone: () => void }) {
  const q = useHolds();
  const canHold = useCan('data_protection.legal_hold');
  const [showReleased, setShowReleased] = useState(false);
  const shown = (q.data ?? []).filter((h) => showReleased || h.releasedAt === null);
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t('p4dp.holds.help')}</p>
      {canHold && draft ? <NewHold subject={draft} onDone={onDraftDone} /> : canHold ? <p className="text-sm text-slate-600">{t('p4dp.holds.noRecords')}</p> : null}
      <CheckboxField label={t('p4dp.holds.showReleased')} checked={showReleased} onChange={(e) => setShowReleased(e.target.checked)} />
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : shown.length === 0 ? (
        <EmptyState message={t('p4dp.holds.empty')} />
      ) : (
        <ul className="space-y-3">
          {shown.map((h) => (
            <li key={h.id}>
              <HoldCard hold={h} canRelease={canHold} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewHold({ subject, onDone }: { subject: DataSubject; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api.post('/data-protection/holds', {
        body: { label: label.trim(), reason: reason.trim(), memberIds: subject.memberIds, visitIds: subject.visitIds },
        schema: LegalHold,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: holdsKey });
      toast(t('p4dp.holds.created'));
      onDone();
    },
  });
  return (
    <Card
      title={t('p4dp.holds.new')}
      description={t('p4dp.holds.fromFind', { members: subject.memberIds.length, visits: subject.visitIds.length })}
      footer={
        <>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={label.trim() === '' || reason.trim() === '' || create.isPending} onClick={() => create.mutate()}>
            {t('p4dp.holds.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <TextField label={t('p4dp.holds.label')} value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} />
        <TextField label={t('p4dp.holds.reason')} value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} />
        <ErrorLine error={create.error} />
      </div>
    </Card>
  );
}

function HoldCard({ hold, canRelease }: { hold: LegalHold; canRelease: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const release = useMutation({
    mutationFn: () => api.post(`/data-protection/holds/${hold.id}/release`, { body: { note: note.trim() }, schema: LegalHold }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: holdsKey });
      toast(t('p4dp.holds.releasedToast'));
      setAsking(false);
    },
  });
  const active = hold.releasedAt === null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">{hold.label}</h3>
        <Badge tone={active ? 'warn' : 'neutral'}>{active ? t('p4dp.holds.active') : t('p4dp.holds.released')}</Badge>
      </div>
      <p className="mt-1 text-sm">{hold.reason}</p>
      <p className="mt-1 text-sm text-slate-600">{t('p4dp.holds.recordsCount', { members: hold.memberIds.length, visits: hold.visitIds.length })}</p>
      <p className="text-sm text-slate-600">{t('p4dp.holds.placedBy', { name: hold.createdByLabel, when: formatDateTime(hold.createdAt) })}</p>
      {hold.releasedAt ? <p className="text-sm text-slate-600">{t('p4dp.holds.releasedBy', { name: hold.releasedByLabel ?? '', when: formatDateTime(hold.releasedAt) })}</p> : null}
      {active && canRelease ? (
        asking ? (
          <div className="mt-3 space-y-2">
            <TextField label={t('p4dp.holds.releaseNote')} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
            <div className="flex gap-2">
              <Button variant="danger" disabled={release.isPending} onClick={() => release.mutate()}>
                {t('p4dp.holds.release')}
              </Button>
              <Button onClick={() => setAsking(false)}>{t('common.cancel')}</Button>
            </div>
            <ErrorLine error={release.error} />
          </div>
        ) : (
          <div className="mt-3">
            <Button onClick={() => setAsking(true)} aria-label={t('p4dp.holds.releaseFor', { label: hold.label })}>
              {t('p4dp.holds.release')}
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}
